import { fetchMedia, FetchMediaError } from 'fetch-media';
import { QueryKey, useQuery, UseQueryOptions } from 'react-query';
import { ReadonlyDeep } from 'type-fest';
import { useLocale } from '../hooks/useLocale';
import { usePrivateConfiguration } from '../hooks/usePrivateConfiguration';
import { useToken } from '../hooks/useToken';
import { authorization } from '../utils/authorization';

export type ApiVotedTracks = ReadonlyDeep<{
  tracks: {
    _links: {
      self: {
        href: string;
      };
      next: {
        href: string;
      };
    };
    _embedded: readonly ApiVotedTrack[];
  };
}>;

export type ApiVotedTrack = ReadonlyDeep<{
  track: {
    _links: {
      self: {
        href: string;
      };
      artist?: { href: string };
      cover_image?: {
        href: string;
        templated: true;
      };
      /*cover?: {
        href: string;
        templated: true;
      };
      poster?: {
        href: string;
        templated: true;
      };*/
      wave?: {
        href: string;
        duration: number;
        templated: true;
      };
      stream?: {
        href: string;
        duration: number;
        templated: true;
      };
    };

    name: string;
    vote: number;
  };
}>;

export function useVotedTracks({
  enabled = true,
  onError,
  ...options
}: UseQueryOptions<ApiVotedTracks, FetchMediaError | Error> = {}) {
  const { data } = usePrivateConfiguration();
  const { ref, token, logout } = useToken();
  const url = data?.configuration._links.my_tracks.href;
  const locale = useLocale();

  return useQuery<ApiVotedTracks, FetchMediaError | Error>(
    [locale, url, 'track-votes'] as QueryKey,
    async ({ signal }) => {
      if (!ref.current) {
        throw new Error('Expected logged in, actual: no token');
      }

      const response = await fetchMedia(url!, {
        headers: {
          accept:
            'application/vnd.soundersmusic.track-vote.v2.collection+json, application/vnd.soundersmusic.track-vote.v1.collection+json; q=0.9',
          acceptLanguage: locale,
          authorization: authorization(ref.current)!,
        },
        method: 'GET',
        disableFormData: true,
        disableFormUrlEncoded: true,
        signal,
      });

      if (response && typeof response === 'object') {
        if ('tracks' in response) {
          return response as ApiVotedTracks;
        }

        throw new FetchMediaError(
          'Expected tracks in response, actual: ' +
            Object.keys(response).join(', '),
          { status: 500, statusText: '' }
        );
      }

      throw new FetchMediaError(
        'Expected tracks response, actual:' + typeof response,
        { status: 500, statusText: '' }
      );
    },
    {
      enabled: Boolean(url && token && enabled),

      onError(error) {
        if (error instanceof FetchMediaError) {
          if (error.response.status === 401) {
            logout();
          }
        }

        if (onError) {
          onError(error);
        }
      },

      ...options,
    }
  );
}

export function useVotedTrack({
  enabled = true,
  onError,
  ...options
}: UseQueryOptions<ApiVotedTrack, FetchMediaError | Error> = {}) {
  const { data } = usePrivateConfiguration();
  const { ref, token, logout } = useToken();
  const url = data?.configuration._links.my_tracks.href;
  const locale = useLocale();

  return useQuery<ApiVotedTrack, FetchMediaError | Error>(
    [locale, url, 'voted-track'] as QueryKey,
    async ({ signal }) => {
      if (!ref.current) {
        throw new Error('Expected logged in, actual: no token');
      }

      const response = await fetchMedia(url!, {
        headers: {
          accept:
            'application/vnd.soundersmusic.track-vote.v2+json, application/vnd.soundersmusic.track-vote.v1+json; q=0.9',
          acceptLanguage: locale,
          authorization: authorization(ref.current)!,
        },
        method: 'GET',
        disableFormData: true,
        disableFormUrlEncoded: true,
        signal,
      });

      if (response && typeof response === 'object') {
        if ('track' in response) {
          return response as ApiVotedTrack;
        }

        throw new FetchMediaError(
          'Expected track in response, actual: ' +
            Object.keys(response).join(', '),
          { status: 500, statusText: '' }
        );
      }

      throw new FetchMediaError(
        'Expected track response, actual:' + typeof response,
        { status: 500, statusText: '' }
      );
    },
    {
      enabled: Boolean(url && token && enabled),

      onError(error) {
        if (error instanceof FetchMediaError) {
          if (error.response.status === 401) {
            logout();
          }
        }

        if (onError) {
          onError(error);
        }
      },

      ...options,
    }
  );
}
